import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ShowCard from '@/components/base/ShowCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { shows } from '@/mocks/shows';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const showId = parseInt(id || '0', 10);
  const show = shows.find((s) => s.id === showId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!show) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center px-4">
            <span className="w-20 h-20 flex items-center justify-center mx-auto mb-4 text-foreground-300">
              <i className="ri-error-warning-line text-5xl"></i>
            </span>
            <h1 className="font-heading text-xl text-foreground-950 mb-2">未找到该美剧</h1>
            <p className="text-foreground-600 mb-6">抱歉，您要查找的美剧暂不存在或已被移除。</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-4-line"></i>
              </span>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedShows = shows
    .filter((s) => s.categorySlug === show.categorySlug && s.id !== show.id)
    .slice(0, 4);

  const breadcrumbItems = [
    { label: '分类浏览', href: '/category/all' },
    { label: show.category, href: `/category/${show.categorySlug}` },
    { label: show.title },
  ];

  return (
    <>
      <SchemaOrg type="Article" data={{
        headline: `${show.title} - ${show.englishTitle} | 人人美剧`,
        description: show.description,
        url: `https://www.renrenmeiju.com/article/${show.id}`,
        datePublished: `${show.year}-01-01`,
        author: { '@type': 'Organization', name: '人人美剧' },
        publisher: { '@type': 'Organization', name: '人人美剧', url: 'https://www.renrenmeiju.com/' },
        about: { '@type': 'Thing', name: show.title },
      }} />

      <Navbar />

      <main className="pt-16">
        <article>
          <div className="relative h-[300px] md:h-[400px] overflow-hidden">
            <img src={show.heroImageUrl} alt={`${show.title} 美剧海报`} title={`${show.title} ${show.englishTitle}`} className="w-full h-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 via-background-950/30 to-transparent"></div>
          </div>

          <div className="px-4 md:px-6 -mt-32 relative z-10">
            <div className="max-w-5xl mx-auto">
              <div className="flex flex-col md:flex-row gap-6 mb-8">
                <div className="w-40 md:w-56 flex-shrink-0 mx-auto md:mx-0">
                  <div className="rounded-lg overflow-hidden border-2 border-background-50 shadow-sm">
                    <img src={show.imageUrl} alt={`${show.title} 海报`} className="w-full aspect-[5/7] object-cover object-top" />
                  </div>
                </div>
                <div className="flex-1 pt-2">
                  <Breadcrumb items={breadcrumbItems} />
                  <h1 className="font-heading text-2xl md:text-3xl text-background-50 md:text-foreground-950 mb-2">
                    {show.title}
                  </h1>
                  <p className="text-sm text-background-50/80 md:text-foreground-600 mb-4">
                    {show.englishTitle}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-500 text-background-50 text-sm font-bold rounded-full whitespace-nowrap">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-xs"></i>
                      </span>
                      {show.rating}
                    </span>
                    <span className="text-sm text-background-50/80 md:text-foreground-600">{show.year}年</span>
                    <span className="text-sm text-background-50/80 md:text-foreground-600">{show.seasons}季</span>
                    <span className={`text-sm px-2 py-0.5 rounded-full whitespace-nowrap ${
                      show.status === '连载中'
                        ? 'bg-accent-100 text-accent-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {show.status}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    {show.tags.map((tag) => (
                      <span key={tag} className="text-xs px-2.5 py-1 bg-background-100 text-foreground-600 rounded-full whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-background-50/70 md:text-foreground-500">
                    <span>导演: {show.director}</span>
                    <span>更新: {show.updateDay}</span>
                    <span>
                      <span className="w-4 h-4 inline-flex items-center justify-center">
                        <i className="ri-eye-line"></i>
                      </span>
                      {(show.views / 10000).toFixed(0)}万次观看
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 py-6 md:py-8">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  <section className="mb-10">
                    <h2 className="font-heading text-lg text-foreground-950 mb-4">剧情简介</h2>
                    <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                      {show.longDescription}
                    </p>
                  </section>

                  <section className="mb-10">
                    <h2 className="font-heading text-lg text-foreground-950 mb-4">主演阵容</h2>
                    <div className="flex flex-wrap gap-3">
                      {show.cast.map((actor) => (
                        <span key={actor} className="text-sm px-3 py-1.5 bg-background-100 text-foreground-700 rounded-lg whitespace-nowrap">
                          {actor}
                        </span>
                      ))}
                    </div>
                  </section>

                  <section className="mb-10">
                    <h2 className="font-heading text-lg text-foreground-950 mb-4">剧集信息</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {[
                        { label: '首播年份', value: `${show.year}年` },
                        { label: '总季数', value: `${show.seasons}季` },
                        { label: '状态', value: show.status },
                        { label: '分类', value: show.category },
                        { label: '导演', value: show.director },
                        { label: '评分', value: show.rating.toString() },
                        { label: '更新频率', value: show.updateDay },
                        { label: '语言', value: '英语原声/中英字幕' },
                      ].map((info) => (
                        <div key={info.label} className="p-3 bg-background-100 rounded-lg">
                          <p className="text-xs text-foreground-500 mb-1">{info.label}</p>
                          <p className="text-sm font-medium text-foreground-950">{info.value}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>

                <aside className="lg:col-span-1">
                  <div className="sticky top-24">
                    <h3 className="font-heading text-base text-foreground-950 mb-4">同类推荐</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-3">
                      {relatedShows.map((rel) => (
                        <ShowCard key={`rel-${rel.id}`} show={rel} size="small" />
                      ))}
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}